import { Box, DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { Modal } from '../modal/modal.component';
import { TextButton, TextButtonColor } from '../text-button/text-button.component';
import { useForm } from 'react-hook-form';
import { FormControl } from '../form-control/form-control.component';
import { Dropdown } from '../dropdown/dropdown.component';
import { TextField } from '../text-field/text-field.component';
import { useEffect, useState } from 'react';
import { SVGIconComponent } from '../icons/types';

export interface ConfirmationModalProps {
  title: string;
  content: string[];
  icon?: SVGIconComponent;
  warning?: string;
  isLoading: boolean;
  noteRequired?: boolean;
  noteMessage?: string;
  errorMessage?: string;
  onConfirm(note?: ConfirmationNote): void;
  onCancel(): void;
  cancelButton?: {
    color?: TextButtonColor;
    label: string;
  };
  actionButton?: {
    color?: TextButtonColor;
    colorOverride?: string;
    label: string;
  };
}

export interface ConfirmationNote {
  content: string;
  visibility: 'private' | 'public';
}

export function ConfirmationModal({
  title,
  content,
  icon,
  warning,
  isLoading,
  noteRequired,
  noteMessage,
  onCancel,
  onConfirm,
  errorMessage,
  actionButton,
  cancelButton
}: ConfirmationModalProps) {
  const [isDisabled, setIsDisabled] = useState(noteRequired);

  const { getValues, formState, control } = useForm<ConfirmationNote>({
    defaultValues: {
      content: '',
      visibility: 'private'
    }
  });

  // If the note is required then watch the formState and disable the confirm button if the form is invalid
  useEffect(() => {
    if (!noteRequired) return;
    setIsDisabled(!formState.isValid);
  }, [noteRequired, formState]);

  const handleConfirm = () => {
    if (noteRequired) {
      // Call the onConfirm callback with the note
      onConfirm({
        content: getValues('content'),
        visibility: getValues('visibility')
      });
    } else {
      // Call the onConfirm callback without a note
      onConfirm();
    }
  };

  return (
    <Modal title={title} open={true} loading={isLoading} maxWidth="xs">
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {errorMessage && (
          <p>
            <Alert variant="outlined" severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              {errorMessage}
            </Alert>
          </p>
        )}
        {content.map((paragraph, index) => {
          if (icon && index === 0) {
            const Icon = icon;
            return (
              <Box
                key={paragraph.slice(0, 20)}
                style={{ gap: '8px', marginBottom: '8px' }}
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icon />
                <Typography variant="body1">{paragraph}</Typography>
              </Box>
            );
          }
          return <p key={paragraph.slice(0, 20)}>{paragraph}</p>;
        })}
        {noteRequired ? <ConfirmationNoteForm noteMessage={noteMessage ?? 'Add a note.'} control={control} /> : null}
        {warning ? (
          <p>
            <Alert variant="outlined" severity="warning" color="error">
              {warning}
            </Alert>
          </p>
        ) : null}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        {cancelButton ? (
          <TextButton color={cancelButton.color || 'secondary'} variant="outlined" onClick={onCancel}>
            {cancelButton.label}
          </TextButton>
        ) : null}
        {actionButton ? (
          <TextButton
            color={actionButton.color || 'primary'}
            variant="outlined"
            disabled={isDisabled}
            onClick={handleConfirm}
            style={
              actionButton.colorOverride
                ? { color: actionButton.colorOverride, borderColor: actionButton.colorOverride }
                : {}
            }
          >
            {actionButton.label}
          </TextButton>
        ) : null}
      </DialogActions>
    </Modal>
  );
}

const notePrivacyOptions: { label: string; value: string }[] = [
  { label: 'Private', value: 'private' },
  { label: 'Public', value: 'public' }
];

export function ConfirmationNoteForm({
  noteMessage,
  control,
  isPrivateOnly
}: {
  noteMessage?: string;
  control: any;
  isPrivateOnly?: boolean;
}) {
  const placeholder = isPrivateOnly ? 'Private note' : 'Note';

  return (
    <Grid container sx={{ flexDirection: 'column' }} spacing={2}>
      <Grid>
        <p>{noteMessage}</p>
      </Grid>
      <Grid>
        <FormControl
          control={control}
          name="content"
          required
          Component={TextField}
          ComponentProps={{
            autoFocus: true,
            fullWidth: true,
            required: true,
            textFieldVariant: 'floating' as const,
            placeholder,
            style: { width: '100%' }
          }}
        />
      </Grid>
      {isPrivateOnly ? null : (
        <Grid>
          <FormControl
            control={control}
            name="visibility"
            required
            ComponentProps={{
              style: { width: '100%' },
              fullWidth: true,
              required: true,
              dropdownVariant: 'floating' as const,
              placeholder: 'Visibility',
              menuItems: notePrivacyOptions
            }}
            Component={Dropdown}
          />
        </Grid>
      )}
    </Grid>
  );
}
